import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import {
  DEFAULT_FOLLOW_GLOBAL_SETTINGS,
  pickFollowGlobalSettings,
} from '@formosa/shared/utils/globalFollowSettings';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  FOLLOW_STATUS_FILTER_OPTIONS,
  FOLLOW_VISIBILITY_LABELS,
  FUTURES_ORDER_TREND_LABELS,
  FUTURES_ORDER_TREND_OPTIONS,
  buildFollowPageParams,
  canDisableFollowOrder,
  defaultFollowFilters,
  formatAmount,
  formatProfitLoss,
} from './followOrderConstants';
import { orderTrendClassName } from '../../utils/orderDisplayFormat';
import FollowOrderCreateModal from './FollowOrderCreateModal';

function ProductPeriodCell({ text }) {
  if (!text) {
    return '—';
  }
  const lines = String(text).split('\n');
  return (
    <div className="product-period-cell">
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
    </div>
  );
}

function SignalsCell({ value }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    if (!value) {
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  if (!value) {
    return '—';
  }

  return (
    <span className="signals-cell">
      <span className="mono-cell">{value}</span>
      <button type="button" className="btn btn-link btn-sm" onClick={handleCopy} title="複製口令">
        {copied ? '已複製' : '複製'}
      </button>
    </span>
  );
}

function GlobalSwitch({ label, checked, disabled, onChange }) {
  return (
    <label className="switch-field">
      <span>{label}</span>
      <span className={`toggle-switch ${checked ? 'is-on' : ''}`}>
        <input
          type="checkbox"
          checked={Boolean(checked)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="toggle-switch__slider" />
      </span>
    </label>
  );
}

function FollowStatusCell({ row, disabling, onDisable }) {
  if (row.statusLabel === '已結束') {
    return <span className="status-text">已結束</span>;
  }
  if (canDisableFollowOrder(row)) {
    return (
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        disabled={disabling}
        onClick={() => onDisable(row)}
      >
        停用
      </button>
    );
  }
  return <span className="status-tag status-tag--muted">停用</span>;
}

export default function FollowOrdersPage() {
  const [filters, setFilters] = useState(defaultFollowFilters());
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [globalSettings, setGlobalSettings] = useState(DEFAULT_FOLLOW_GLOBAL_SETTINGS);
  const [savingSettings, setSavingSettings] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [disableTarget, setDisableTarget] = useState(null);
  const [disabling, setDisabling] = useState(false);

  async function loadGlobalSettings() {
    try {
      const data = await adminApi.getGlobalFollowConfig();
      setGlobalSettings(pickFollowGlobalSettings(data?.content));
    } catch (err) {
      setError(err.message || '載入全局開關失敗');
    }
  }

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.followOrderPage(buildFollowPageParams(pageNo, nextFilters));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadGlobalSettings();
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = defaultFollowFilters();
    setFilters(reset);
    loadRecords(1, reset);
  }

  async function handleGlobalSettingChange(key, value) {
    const next = { ...globalSettings, [key]: value };
    setGlobalSettings(next);
    setSavingSettings(true);
    setError('');
    try {
      const saved = await adminApi.saveGlobalFollowConfig({
        futureFollowOpen: next.futureFollowOpen,
        followSignalsOpen: next.followSignalsOpen,
        showFollowOrderButton: next.showFollowOrderButton,
      });
      setGlobalSettings(pickFollowGlobalSettings(saved?.content));
    } catch (err) {
      setError(err.message || '更新全局開關失敗');
      await loadGlobalSettings();
    } finally {
      setSavingSettings(false);
    }
  }

  async function handleConfirmDisable() {
    if (!disableTarget?.id) {
      return;
    }
    setDisabling(true);
    setError('');
    try {
      await adminApi.disableFollowOrder(disableTarget.id);
      setDisableTarget(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '停用失敗');
    } finally {
      setDisabling(false);
    }
  }

  return (
    <div className="page-card">
      <h2>跟單紀錄</h2>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="followRoundCode">週期 ID</label>
          <input
            id="followRoundCode"
            value={filters.roundCode}
            onChange={(e) => updateFilter('roundCode', e.target.value)}
            placeholder="請輸入週期 ID"
          />
        </div>

        <div className="form-field">
          <label htmlFor="followOrderTrend">投注類型</label>
          <select
            id="followOrderTrend"
            value={filters.orderTrend}
            onChange={(e) => updateFilter('orderTrend', e.target.value)}
          >
            {FUTURES_ORDER_TREND_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="followState">狀態</label>
          <select
            id="followState"
            value={filters.state}
            onChange={(e) => updateFilter('state', e.target.value)}
          >
            {FOLLOW_STATUS_FILTER_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="followCreateFrom">發佈時間起</label>
          <input
            id="followCreateFrom"
            type="datetime-local"
            value={filters.createTimeFromInput}
            onChange={(e) => updateFilter('createTimeFromInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="followCreateTo">發佈時間迄</label>
          <input
            id="followCreateTo"
            type="datetime-local"
            value={filters.createTimeToInput}
            onChange={(e) => updateFilter('createTimeToInput', e.target.value)}
          />
        </div>

        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
            新增
          </button>
        </div>
      </form>

      <div className="global-switch-bar">
        <GlobalSwitch
          label="交割跟單開關"
          checked={globalSettings.futureFollowOpen}
          disabled={savingSettings}
          onChange={(value) => handleGlobalSettingChange('futureFollowOpen', value)}
        />
        <GlobalSwitch
          label="口令啟用開關"
          checked={globalSettings.followSignalsOpen}
          disabled={savingSettings}
          onChange={(value) => handleGlobalSettingChange('followSignalsOpen', value)}
        />
        <GlobalSwitch
          label="投注頁顯示跟單開關"
          checked={globalSettings.showFollowOrderButton}
          disabled={savingSettings}
          onChange={(value) => handleGlobalSettingChange('showFollowOrderButton', value)}
        />
      </div>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-scroll">
        <table className="data-table data-table--wide">
          <thead>
            <tr>
              <th>標題</th>
              <th>週期 ID</th>
              <th>產品名稱_週期</th>
              <th>投注類型</th>
              <th>發佈類型</th>
              <th>跟單人數</th>
              <th>實際跟單金額</th>
              <th>跟單費用</th>
              <th>跟單金額</th>
              <th>盈虧金額</th>
              <th>發佈時間</th>
              <th>週期開始</th>
              <th>週期結束</th>
              <th>動態口令</th>
              <th>狀態</th>
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={15} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => {
                const profitLoss = formatProfitLoss(row.profitLoss);
                const trendLabel =
                  FUTURES_ORDER_TREND_LABELS[row.orderTrend] || row.orderTrend || '—';

                return (
                  <tr key={row.id}>
                    <td>{row.name || '—'}</td>
                    <td className="mono-cell">{row.roundCode || '—'}</td>
                    <td>
                      <ProductPeriodCell text={row.productNamePeriod} />
                    </td>
                    <td>
                      <span className={orderTrendClassName(row.orderTrend)}>{trendLabel}</span>
                    </td>
                    <td>
                      {FOLLOW_VISIBILITY_LABELS[row.visibilityType] || row.visibilityType || '—'}
                    </td>
                    <td>{row.memberCount ?? '—'}</td>
                    <td>{formatAmount(row.actualFollowAmount)}</td>
                    <td>{formatAmount(row.totalFollowFee)}</td>
                    <td>{row.followAmountConfig || '—'}</td>
                    <td className={profitLoss.className}>{profitLoss.text}</td>
                    <td>{formatDateTime(row.createTime)}</td>
                    <td>{formatDateTime(row.startTime)}</td>
                    <td>{formatDateTime(row.endTime)}</td>
                    <td>
                      <SignalsCell value={row.signals} />
                    </td>
                    <td>
                      <FollowStatusCell
                        row={row}
                        disabling={disabling && disableTarget?.id === row.id}
                        onDisable={setDisableTarget}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => loadRecords(p, filters)} />

      <FollowOrderCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => loadRecords(1, filters)}
      />

      <ConfirmDialog
        open={Boolean(disableTarget)}
        title="確認停用"
        detail={disableTarget ? `標題：${disableTarget.name || '—'}` : ''}
        message="停用後會員將無法再跟隨此配置，是否繼續？"
        confirmLabel="確認停用"
        confirming={disabling}
        onConfirm={handleConfirmDisable}
        onClose={() => {
          if (!disabling) {
            setDisableTarget(null);
          }
        }}
      />
    </div>
  );
}
